/**
 * Related Products Section v2
 *
 * Custom element host for the Related Products PDP section. Handles the
 * "Select a Shade" toggle, swatch selection, image swapping, and AJAX
 * add-to-cart with cart-drawer integration.
 *
 * Listeners are scoped to `this`, so they auto-clean up on disconnect (Theme
 * Editor reload, etc.). Post-add cart sync mirrors `refreshCartAndOpen`:
 * liquidAjaxCart.update → section HTML refresh → cart-open (in that order).
 */
const RP_SELECTORS = {
  shadeSelector: '.related-products__shade-selector',
  selectShadeBtn: '.js-rp-select-shade',
  swatchesPanel: '[data-swatches-panel]',
  swatch: '.related-products__swatch',
  shadeName: '[data-shade-name]',
  addBtn: '.js-rp-add-to-bag',
  cardImg: '.related-products__card-img',
};

/** @returns {Promise<boolean>} true when liquidAjaxCart.update ran */
function rpSyncLiquidAjaxCart() {
  return new Promise((resolve) => {
    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.update === 'function') {
      window.liquidAjaxCart.update({}, { lastCallback: () => resolve(true) });
    } else {
      resolve(false);
    }
  });
}

/** Re-fetch `[data-ajax-cart-section]` regions (header drawer) after raw cart/add.js. */
function rpRefreshAjaxCartSectionsAsync() {
  const jobs = Array.from(document.querySelectorAll('[data-ajax-cart-section]')).map((cartEl) => {
    const section = cartEl.closest('[id^="shopify-section-"]');
    if (!section) return Promise.resolve();
    const sid = section.id.replace('shopify-section-', '');
    return fetch(`${window.location.pathname}?sections=${sid}`)
      .then((r) => r.json())
      .then((data) => {
        if (!data[sid]) return;
        const doc = new DOMParser().parseFromString(data[sid], 'text/html');
        const fresh = doc.querySelector('[data-ajax-cart-section]');
        if (fresh) cartEl.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  });
  return Promise.all(jobs);
}

/**
 * When liquidAjaxCart is unavailable, sync header count binders from cart.js.
 * LAC is loaded globally in layout/theme.liquid (including paid-social); this
 * is a defensive fallback only (e.g. module race on first click).
 */
function rpSyncCartCountBinders() {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((cart) => {
      const count = cart.item_count ?? 0;
      document.querySelectorAll('[data-ajax-cart-bind="item_count"]').forEach((el) => {
        el.textContent = String(count);
        el.classList.toggle('hidec', count === 0);
        el.classList.toggle('showc', count > 0);
      });
    })
    .catch(() => {});
}

/**
 * Full cart UI sync after raw fetch add — awaited before opening the drawer.
 * Uses the same steps as `window.JR.refreshCartAndOpen` but waits for each
 * async step (JR's helper fires cart-open immediately and is not used here).
 */
function rpRefreshCartAndOpen() {
  return rpSyncLiquidAjaxCart()
    .then((lacSynced) => {
      if (!lacSynced) return rpSyncCartCountBinders();
    })
    .then(rpRefreshAjaxCartSectionsAsync)
    .then(() => {
      window.dispatchEvent(new CustomEvent('cart-open'));
    });
}

/** Matches section-product.js setAtcLoading / cart-tiered-gwp raw-fetch ATC. */
const RP_ADD_LOADING_LABEL = 'Adding...';

/** Card-level direct add matches live; expanded swatch panel keeps "Add to Bag". */
function rpAddButtonDefaultLabel(btn) {
  return btn.closest(RP_SELECTORS.swatchesPanel) ? 'Add to Bag' : 'Add to Cart';
}

function rpResetAddButton(btn) {
  btn.classList.remove('is-adding');
  btn.disabled = false;
  btn.textContent = rpAddButtonDefaultLabel(btn);
}

class RelatedProductsV2 extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll(RP_SELECTORS.shadeSelector).forEach((selector) => this.initShadeSelector(selector));

    // Top-level direct-add buttons (single variant) and no-shade-option cards.
    this.querySelectorAll(RP_SELECTORS.addBtn).forEach((btn) => {
      if (!btn.closest(RP_SELECTORS.swatchesPanel)) {
        btn.addEventListener('click', this.handleAddToBag);
      }
    });
  }

  initShadeSelector(selector) {
    const selectBtn = selector.querySelector(RP_SELECTORS.selectShadeBtn);
    const panel = selector.querySelector(RP_SELECTORS.swatchesPanel);

    if (selectBtn && panel) {
      selectBtn.addEventListener('click', () => {
        // Only one quick-shop open at a time: close any other open selector first.
        this.querySelectorAll(RP_SELECTORS.shadeSelector).forEach((other) => {
          if (other !== selector) this.closeSelector(other);
        });
        this.openSelector(selector);
      });
    }

    selector.querySelectorAll(RP_SELECTORS.swatch).forEach((swatch) => {
      swatch.addEventListener('click', () => this.handleSwatchClick(swatch, selector));
    });

    const addBtn = selector.querySelector(RP_SELECTORS.addBtn);
    if (addBtn) addBtn.addEventListener('click', this.handleAddToBag);
  }

  /** Reveal a selector's swatch/size panel and hide the card's price/stars. */
  openSelector(selector) {
    const selectBtn = selector.querySelector(RP_SELECTORS.selectShadeBtn);
    const panel = selector.querySelector(RP_SELECTORS.swatchesPanel);
    if (!selectBtn || !panel) return;
    selectBtn.style.display = 'none';
    panel.style.display = '';
    const cardInfo = selector.closest('.related-products__card-info');
    if (cardInfo) cardInfo.classList.add('is-shades-open');
    const priceEl = cardInfo?.querySelector('.related-products__card-price');
    if (priceEl) priceEl.style.display = 'none';
    const starsEl = cardInfo?.querySelector('.related-products__card-stars');
    if (starsEl) starsEl.style.display = 'none';
  }

  /** Collapse a selector back to its "Select …" button + restore price/stars. */
  closeSelector(selector) {
    const selectBtn = selector.querySelector(RP_SELECTORS.selectShadeBtn);
    const panel = selector.querySelector(RP_SELECTORS.swatchesPanel);
    if (!selectBtn || !panel) return;
    // Only act on selectors that are actually open.
    if (panel.style.display === 'none') return;
    panel.style.display = 'none';
    selectBtn.style.display = '';
    const cardInfo = selector.closest('.related-products__card-info');
    if (cardInfo) cardInfo.classList.remove('is-shades-open');
    const priceEl = cardInfo?.querySelector('.related-products__card-price');
    if (priceEl) priceEl.style.display = '';
    const starsEl = cardInfo?.querySelector('.related-products__card-stars');
    if (starsEl) starsEl.style.display = '';
  }

  handleSwatchClick(swatch, container) {
    // Sold-out swatches render a diagonal slash and are flagged with both
    // `aria-disabled` and `data-shade-unavailable` from the Liquid template.
    // Bail out so we don't let the user "select" an unbuyable variant.
    if (swatch.classList.contains('related-products__swatch--unavailable') ||
        swatch.dataset.shadeUnavailable === 'true' ||
        swatch.getAttribute('aria-disabled') === 'true') {
      return;
    }

    container.querySelectorAll(RP_SELECTORS.swatch).forEach((s) => {
      s.classList.remove('related-products__swatch--active');
    });
    swatch.classList.add('related-products__swatch--active');

    const shadeName = container.querySelector(RP_SELECTORS.shadeName);
    if (shadeName) shadeName.textContent = swatch.dataset.shadeValue || '';

    const variantId = swatch.dataset.variantId;
    const addBtn = container.querySelector(RP_SELECTORS.addBtn);
    if (addBtn && variantId) {
      addBtn.dataset.variantId = variantId;
      addBtn.disabled = false;
      addBtn.textContent = rpAddButtonDefaultLabel(addBtn);
    }

    const variantImage = swatch.dataset.variantImage;
    if (variantImage) {
      const card = container.closest('.related-products__card');
      const img = card?.querySelector(RP_SELECTORS.cardImg);
      if (img) {
        img.src = variantImage;
        img.srcset = `${variantImage} 540w`;
      }
    }
  }

  /** `currentTarget` keeps the bound button reference even after the click bubbles. */
  handleAddToBag = (e) => {
    const btn = e.currentTarget;
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.disabled || btn.classList.contains('is-adding')) return;

    btn.classList.add('is-adding');
    btn.disabled = true;
    btn.textContent = RP_ADD_LOADING_LABEL;

    const items = [{ id: parseInt(variantId, 10), quantity: 1 }];

    // Button briefly confirms with "Added", then returns to its own default
    // label ("Add to Cart" vs "Add to Bag") while keeping the swatch panel open.
    const onAdded = () => {
      btn.textContent = 'Added';
      btn.classList.remove('is-adding');
      btn.disabled = false;
      setTimeout(() => {
        btn.textContent = rpAddButtonDefaultLabel(btn);
      }, 1200);
    };
    const onError = () => {
      btn.textContent = 'Error';
      setTimeout(() => rpResetAddButton(btn), 1500);
    };

    // Preferred path: liquidAjaxCart.add bundles the add AND the section
    // re-render into ONE cart request, and fires request-end — which
    // component-cart-drawer.js already handles (opens the drawer + syncs the
    // header count). The legacy path below did a raw add.js followed by a
    // separate liquidAjaxCart.update({}) — two serialized cart writes (the slow
    // add.js + update.js pair seen in the network tab).
    if (window.liquidAjaxCart?.add) {
      window.liquidAjaxCart.add(
        { items },
        {
          info: { initiator: 'related-products-v2' },
          lastCallback: (requestState) => {
            if (requestState?.responseData?.ok) onAdded();
            else onError();
          },
        }
      );
      return;
    }

    // Fallback (liquidAjaxCart unavailable): raw add + manual cart sync.
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(() => rpRefreshCartAndOpen())
      .then(onAdded)
      .catch(onError);
  };

  resetShadeSelector(btn) {
    const selector = btn.closest(RP_SELECTORS.shadeSelector);
    if (!selector) return;

    const selectBtn = selector.querySelector(RP_SELECTORS.selectShadeBtn);
    const panel = selector.querySelector(RP_SELECTORS.swatchesPanel);
    if (selectBtn && panel) {
      panel.style.display = 'none';
      selectBtn.style.display = '';
    }

    const cardInfo = selector.closest('.related-products__card-info');
    if (cardInfo) cardInfo.classList.remove('is-shades-open');
    const priceEl = cardInfo?.querySelector('.related-products__card-price');
    if (priceEl) priceEl.style.display = '';
    /* Restore the review stars hidden when the panel was opened so the
       card returns to its initial title / stars / price / button layout. */
    const starsEl = cardInfo?.querySelector('.related-products__card-stars');
    if (starsEl) starsEl.style.display = '';
  }
}

if (!customElements.get('related-products-v2')) {
  customElements.define('related-products-v2', RelatedProductsV2);
}
